import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import supplierRepository from '../../services/repositories/supplier.repository';
import { buildImageUrl } from '../../utils/imageUrl';
import { Button, ButtonGroup } from 'react-bootstrap';
import '../../styles/products.scss';

const SupplierIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [error, setError] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await supplierRepository.getAll();
      const suppliersData = response.data || response;

      const formattedSuppliers = Array.isArray(suppliersData) ? suppliersData.map(supplier => {
        const imageUrl = buildImageUrl(supplier.image);

        return {
          ...supplier,
          image: imageUrl ? [imageUrl] : [],
        };
      }) : [];

      setSuppliers(formattedSuppliers);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError(err.message || 'Erreur lors du chargement des fournisseurs');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Image',
      accessor: 'image',
      type: 'image',
      sortable: false,
    },
    {
      header: 'Nom',
      accessor: 'name',
      sortable: true,
    },
    {
      header: 'Email',
      accessor: 'email',
      sortable: true,
    },
    {
      header: 'Téléphone',
      accessor: 'phone',
      sortable: false,
      render: (value) => value || '-',
    },
    {
      header: 'Adresse',
      accessor: 'address',
      sortable: false,
      render: (value) => value || '-',
    },
    {
      header: 'Type',
      accessor: 'type',
      sortable: true,
      render: (value) => (
        <span className={`badge ${value === 'local' ? 'bg-success' : 'bg-info'}`}>
          {value === 'local' ? 'Local' : 'International'}
        </span>
      ),
    },
    {
      header: 'Statut',
      accessor: 'status',
      sortable: true,
      render: (value) => (
        <span className={`badge ${value === 'active' ? 'bg-success' : 'bg-warning'}`}>
          {value === 'active' ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/suppliers/supplier-add');
  }, [navigate]);

  const handleEdit = useCallback((supplier) => {
    navigate(`/admin/suppliers/supplier-edit/${supplier.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (supplier) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) {
      try {
        setLoading(true);
        await supplierRepository.delete(supplier.id);
        await fetchSuppliers();
      } catch (err) {
        console.error('Error deleting supplier:', err);
        alert('Erreur lors de la suppression du fournisseur');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchSuppliers]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} fournisseurs ?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await supplierRepository.delete(id);
        }
        alert(`${selectedIds.length} fournisseurs supprimés avec succès`);
        await fetchSuppliers();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Erreur lors de la suppression des fournisseurs');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchSuppliers]);

  useEffect(() => {
    setTitle('Liste des Fournisseurs');
    fetchSuppliers();
  }, [setTitle, fetchSuppliers]);

  return (
    <div className="supplier-list-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      <DataTable
        data={suppliers}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title="Liste des Fournisseurs"
        addButtonText="Nouveau Fournisseur"
        searchPlaceholder="Rechercher des fournisseurs..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default SupplierIndex;